// Client for the pokervane_web shared high-score hub.
// See doc/HIGH_SCORES_INTEGRATION.md for the endpoint contract.
import { GAME, HUB, NAME_KEY, PW_SET_KEY, TOKEN_KEY } from "config";

export type NameStatus =
  | "available"
  | "owned_by_you"
  | "needs_password"
  | "taken"
  | "not_allowed";

export type BoardPeriod = "all_time" | "weekly";

export type BoardEntry = { name: string; score: number; at: string };

export type SubmitResult =
  | { ok: true }
  | { ok: false; status: number; error?: string };

// Get-or-create the per-browser device token. The hub stores only a SHA-256
// digest of it; it proves ownership of a username on this device.
const randomToken = (): string => {
  // crypto.randomUUID isn't in this project's TS DOM lib; guard + fall back.
  const c = typeof crypto !== "undefined" ? (crypto as any) : undefined;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const deviceToken = (): string => {
  const existing = localStorage.getItem(TOKEN_KEY);
  if (existing) return existing;
  const t = randomToken();
  localStorage.setItem(TOKEN_KEY, t);
  return t;
};

export const lastName = (): string => localStorage.getItem(NAME_KEY) ?? "";

export const passwordWasSet = (): boolean =>
  localStorage.getItem(PW_SET_KEY) === "1";

// Check a name before submitting, to guide the user. Falls back to "taken" on a
// network error so the UI errs toward asking for a different name.
export const nameStatus = async (name: string): Promise<NameStatus> => {
  try {
    const res = await fetch(`${HUB}/high_scores/name_status`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ game: GAME, name, token: deviceToken() }),
    });
    const data = await res.json().catch(() => ({}));
    return (data.status as NameStatus) ?? "taken";
  } catch {
    return "taken";
  }
};

// Submit a score. On 201 the name is claimed/verified; we persist the name and
// whether a password was set. Otherwise returns the hub's status + error.
export const submitScore = async (
  name: string,
  score: number,
  moveLog: string,
  password?: string
): Promise<SubmitResult> => {
  let res: Response;
  try {
    res = await fetch(`${HUB}/high_scores`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        game: GAME,
        name,
        score,
        move_log: moveLog,
        token: deviceToken(),
        password,
      }),
    });
  } catch {
    return { ok: false, status: 0, error: "Network error. Try again." };
  }

  if (res.status === 201) {
    localStorage.setItem(NAME_KEY, name);
    localStorage.setItem(PW_SET_KEY, password ? "1" : "0");
    return { ok: true };
  }

  const data = await res.json().catch(() => ({}));
  return { ok: false, status: res.status, error: data.error };
};

// Fetch a leaderboard. The response key equals the requested period.
export const getBoard = async (
  period: BoardPeriod = "all_time",
  limit = 10
): Promise<BoardEntry[]> => {
  const res = await fetch(
    `${HUB}/high_scores?game=${GAME}&period=${period}&limit=${limit}`
  );
  const data = await res.json().catch(() => ({}));
  return (data[period] as BoardEntry[]) ?? [];
};
